/**
 * Cockpit de resposta do atendimento — o rodapé do chat, no desenho do WhatsApp: emoji, anexos
 * (foto/vídeo, documento, figurinha), gravação de áudio, ditado por voz e envio com Enter.
 *
 * O que FUNCIONA: texto, emoji, ditado (Web Speech API do navegador, pt-BR), resposta rápida e,
 * quando o contexto passa `onSendWithAttachments` (conversa de WhatsApp, C6), o envio de foto,
 * vídeo, documento, figurinha e áudio gravado pelo `sendMedia` do WAPI da TDZAIN.
 * Contextos SEM esse caminho (grupos: o WAPI só tem envio de texto para grupo) mantêm o bloqueio
 * explícito dos anexos com aviso. Fingir que enviou seria pior do que declarar a ausência.
 */
import React, { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { FileText, Image as ImageIcon, Mic, Paperclip, Send, Smile, Square, Sticker, X } from "lucide-react";
import { Button, controlClass } from "../ui/index.js";

const EMOJIS = [
  "😀", "😁", "😂", "🙂", "😉", "😊", "😍", "🤝", "🙏", "👍", "👏", "💪", "🔥", "✅", "❌", "⚠️",
  "📦", "🚚", "📍", "🛠️", "⚡", "💰", "💳", "🧾", "📞", "📅", "⏰", "⭐", "🎉", "😅", "🤔", "👋"
];

export type PendingAttachment = { id: string; kind: "image" | "document" | "sticker" | "audio"; name: string; url: string | null; file: File };

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

function speechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const scope = window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

const ATTACH_KIND_LABEL: Record<PendingAttachment["kind"], string> = { image: "Foto/vídeo", document: "Documento", sticker: "Figurinha", audio: "Áudio" };

function ToolButton({ label, active = false, disabled = false, onClick, children }: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }): ReactElement {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={
        "grid h-[40px] w-[40px] shrink-0 place-items-center rounded-pill transition-colors disabled:opacity-40 " +
        (active ? "bg-brand text-on-brand" : "text-muted hover:bg-surface-soft hover:text-ink")
      }
    >
      {children}
    </button>
  );
}

export function ChatCockpit({
  value,
  onChange,
  onSend,
  onSendWithAttachments,
  sending = false,
  error = null,
  sideActions = null,
  placeholder = "Escreva uma mensagem…",
  textareaId = "chat-text",
  textareaLabel = "Mensagem"
}: {
  value: string;
  onChange: (next: string) => void;
  onSend: () => void;
  /**
   * Envio COM anexos (C6). Presente = anexos deixam de bloquear o envio; o cockpit os entrega aqui
   * (junto do texto atual em `value`) e só os limpa se a promessa resolver — em erro, ficam para nova tentativa.
   */
  onSendWithAttachments?: (attachments: PendingAttachment[]) => Promise<void>;
  sending?: boolean;
  error?: string | null;
  /** Ações específicas do contexto (resposta rápida, devolver à IA…), à esquerda do envio. */
  sideActions?: ReactNode;
  placeholder?: string;
  textareaId?: string;
  textareaLabel?: string;
}): ReactElement {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [recording, setRecording] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const SpeechCtor = speechRecognitionCtor();
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

  // Libera microfone/ditado e URLs de pré-visualização ao sair da conversa.
  useEffect(
    () => () => {
      recognitionRef.current?.stop();
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    },
    []
  );

  const addAttachment = (kind: PendingAttachment["kind"], file: File): void => {
    const url = kind === "image" || kind === "sticker" || kind === "audio" ? URL.createObjectURL(file) : null;
    setAttachments((current) => [...current, { id: `${Date.now()}_${current.length}`, kind, name: file.name, url, file }]);
    setAttachOpen(false);
  };

  const pickFile = (kind: PendingAttachment["kind"], accept: string): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) addAttachment(kind, file);
    };
    input.click();
  };

  const toggleRecording = async (): Promise<void> => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => chunksRef.current.push(event.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        addAttachment("audio", new File([blob], `audio-${new Date().toISOString().slice(11, 19).replace(/:/g, "")}.webm`, { type: blob.type }));
        setRecording(false);
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setNotice(null);
    } catch {
      setNotice("Não foi possível acessar o microfone. Libere a permissão no navegador.");
    }
  };

  const toggleDictation = (): void => {
    if (dictating) {
      recognitionRef.current?.stop();
      return;
    }
    if (!SpeechCtor) return;
    const recognition = new SpeechCtor();
    recognition.lang = "pt-BR";
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      let spoken = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result?.isFinal) spoken += result[0]?.transcript ?? "";
      }
      const text = spoken.trim();
      if (!text) return;
      const current = valueRef.current;
      onChange(current && !current.endsWith(" ") && !current.endsWith("\n") ? `${current} ${text}` : `${current}${text}`);
    };
    recognition.onend = () => setDictating(false);
    recognition.onerror = () => {
      setDictating(false);
      setNotice("O ditado foi interrompido. Verifique a permissão do microfone.");
    };
    recognitionRef.current = recognition;
    recognition.start();
    setDictating(true);
    setNotice(null);
  };

  const hasText = value.trim().length > 0;
  const canSendMedia = onSendWithAttachments !== undefined;
  const blockedByMedia = attachments.length > 0 && !canSendMedia;
  const canSubmit = !sending && !blockedByMedia && (hasText || (canSendMedia && attachments.length > 0));
  const submit = (): void => {
    if (!canSubmit) return;
    if (canSendMedia && attachments.length > 0) {
      void onSendWithAttachments(attachments).then(() => setAttachments([]), () => undefined);
      return;
    }
    onSend();
  };
  return (
    <div className="flex flex-col gap-2">
      {attachments.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label="Anexos pendentes" data-pending-attachments={attachments.length}>
          {attachments.map((item) => (
            <li key={item.id} className="flex items-center gap-2 rounded-control border border-border bg-surface-soft px-2 py-1.5 text-sm">
              {item.kind === "audio" && item.url ? (
                <audio controls src={item.url} className="h-[32px] max-w-[14rem]" />
              ) : item.url ? (
                <img src={item.url} alt="" className="h-[40px] w-[40px] rounded-control object-cover" />
              ) : (
                <FileText className="h-5 w-5 text-muted" aria-hidden focusable={false} />
              )}
              <span className="flex flex-col leading-tight">
                <span className="max-w-[10rem] truncate font-bold text-ink">{item.name}</span>
                <span className="text-xs text-muted">{ATTACH_KIND_LABEL[item.kind]}</span>
              </span>
              <button type="button" aria-label={`Remover ${item.name}`} onClick={() => setAttachments((current) => current.filter((other) => other.id !== item.id))} className="rounded-pill p-1 text-muted hover:bg-surface hover:text-ink">
                <X className="h-4 w-4" aria-hidden focusable={false} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative flex items-end gap-1 rounded-panel border border-border-strong bg-surface p-1.5">
        <ToolButton label="Emojis" active={emojiOpen} onClick={() => { setEmojiOpen((open) => !open); setAttachOpen(false); }}>
          <Smile className="h-5 w-5" aria-hidden focusable={false} />
        </ToolButton>
        <ToolButton label="Anexar" active={attachOpen} onClick={() => { setAttachOpen((open) => !open); setEmojiOpen(false); }}>
          <Paperclip className="h-5 w-5" aria-hidden focusable={false} />
        </ToolButton>
        {emojiOpen ? (
          <div role="dialog" aria-label="Emojis" className="absolute bottom-full left-0 z-20 mb-2 grid w-72 grid-cols-8 gap-1 rounded-panel border border-border bg-surface p-3 shadow-s2">
            {EMOJIS.map((emoji) => (
              <button key={emoji} type="button" aria-label={`Inserir ${emoji}`} onClick={() => onChange(value + emoji)} className="grid h-[32px] w-[32px] place-items-center rounded-control text-xl hover:bg-surface-soft">
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
        {attachOpen ? (
          <div role="menu" aria-label="Anexar" className="absolute bottom-full left-12 z-20 mb-2 flex w-52 flex-col rounded-panel border border-border bg-surface p-1.5 shadow-s2">
            <button type="button" role="menuitem" onClick={() => pickFile("image", "image/*,video/*")} className="flex items-center gap-3 rounded-control px-3 py-2 text-left text-sm font-bold text-ink hover:bg-surface-soft">
              <ImageIcon className="h-4 w-4 text-info" aria-hidden focusable={false} /> Foto ou vídeo
            </button>
            <button type="button" role="menuitem" onClick={() => pickFile("document", ".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip")} className="flex items-center gap-3 rounded-control px-3 py-2 text-left text-sm font-bold text-ink hover:bg-surface-soft">
              <FileText className="h-4 w-4 text-brand-text" aria-hidden focusable={false} /> Documento
            </button>
            <button type="button" role="menuitem" onClick={() => pickFile("sticker", "image/webp,image/png")} className="flex items-center gap-3 rounded-control px-3 py-2 text-left text-sm font-bold text-ink hover:bg-surface-soft">
              <Sticker className="h-4 w-4 text-success" aria-hidden focusable={false} /> Figurinha
            </button>
          </div>
        ) : null}
        <label className="sr-only" htmlFor={textareaId}>{textareaLabel}</label>
        <textarea
          id={textareaId}
          value={value}
          rows={1}
          maxLength={4000}
          placeholder={recording ? "Gravando áudio…" : dictating ? "Ouvindo… fale agora" : placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className={controlClass + " max-h-40 min-h-[40px] flex-1 resize-none border-0 bg-transparent py-2 shadow-none focus-visible:outline-none"}
        />
        <ToolButton label={dictating ? "Parar ditado" : SpeechCtor ? "Ditar mensagem (voz para texto)" : "Ditado indisponível neste navegador"} active={dictating} disabled={!SpeechCtor} onClick={toggleDictation}>
          <Mic className="h-5 w-5" aria-hidden focusable={false} />
        </ToolButton>
        <ToolButton label={recording ? "Parar e anexar áudio" : canRecord ? "Gravar áudio" : "Gravação indisponível neste navegador"} active={recording} disabled={!canRecord} onClick={() => void toggleRecording()}>
          {recording ? <Square className="h-4 w-4" aria-hidden focusable={false} /> : <span aria-hidden className="h-3 w-3 rounded-pill bg-danger" />}
        </ToolButton>
        <Button type="button" variant="primary" disabled={!canSubmit} onClick={submit} aria-label="Enviar mensagem">
          <Send className="h-4 w-4" aria-hidden focusable={false} />
          {sending ? "Enviando…" : "Enviar"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">{sideActions}</div>
        <span className="text-xs text-muted">Enter envia · Shift+Enter quebra a linha</span>
      </div>
      {blockedByMedia ? (
        <p className="m-0 rounded-control bg-warning-soft px-3 py-2 text-sm text-ink" role="status" data-status="awaiting_source" data-media-send="blocked">
          Envio de anexo, áudio e figurinha aguardando contrato: o WAPI da TDZAIN hoje só envia texto. Remova os anexos para enviar a mensagem.
        </p>
      ) : null}
      {notice ? <p className="m-0 text-sm text-muted" role="status">{notice}</p> : null}
      {error ? <p className="m-0 text-sm text-danger" role="alert">{error}</p> : null}
    </div>
  );
}
